import { BillingSubscriptionDTO, InvestorListDTO, UserDTO } from '@inversor-hub/types';

export interface InvestorInvestmentAccessState {
  isPlanRestricted: boolean;
  maxOwnedInvestments: number | null;
  ownedInvestments: InvestorListDTO[];
  lockedInvestmentIds: string[];
  primaryAccessibleInvestmentId?: string;
  canCreateInvestment: boolean;
  shouldShowUpgradePrompt: boolean;
}

const EMPTY_ACCESS_STATE: InvestorInvestmentAccessState = {
  isPlanRestricted: false,
  maxOwnedInvestments: null,
  ownedInvestments: [],
  lockedInvestmentIds: [],
  primaryAccessibleInvestmentId: undefined,
  canCreateInvestment: true,
  shouldShowUpgradePrompt: false,
};

const sortInvestmentsByCreatedAt = (investments: InvestorListDTO[]) =>
  [...investments].sort((left, right) => {
    const leftTime = new Date(left.createdAt).getTime();
    const rightTime = new Date(right.createdAt).getTime();
    if (leftTime === rightTime) {
      return left.id.localeCompare(right.id);
    }

    return leftTime - rightTime;
  });

export const getInvestorInvestmentAccessState = ({
  user,
  billingSubscription,
  investments,
}: {
  user?: UserDTO;
  billingSubscription?: BillingSubscriptionDTO;
  investments?: InvestorListDTO[];
}): InvestorInvestmentAccessState => {
  if (!user || user.role !== 'investor') {
    return EMPTY_ACCESS_STATE;
  }

  const ownedInvestments = sortInvestmentsByCreatedAt(
    (investments ?? []).filter((investment) => investment.investorId === user.id)
  );
  const maxOwnedInvestments = billingSubscription?.maxOwnedInvestments ?? 0;
  const accessibleInvestments =
    maxOwnedInvestments === null
      ? ownedInvestments
      : ownedInvestments.slice(0, maxOwnedInvestments);
  const lockedInvestments =
    maxOwnedInvestments === null ? [] : ownedInvestments.slice(maxOwnedInvestments);
  const canCreateInvestment =
    maxOwnedInvestments === null || ownedInvestments.length < maxOwnedInvestments;

  return {
    isPlanRestricted: maxOwnedInvestments !== null,
    maxOwnedInvestments,
    ownedInvestments,
    lockedInvestmentIds: lockedInvestments.map((investment) => investment.id),
    primaryAccessibleInvestmentId: accessibleInvestments[0]?.id,
    canCreateInvestment,
    shouldShowUpgradePrompt: !canCreateInvestment || lockedInvestments.length > 0,
  };
};
